'use client';

import InlineStyle from '@/components/InlineStyle';
import { motion } from 'framer-motion';
import { Activity, BarChart3, Clock, MessageSquareWarning, Radar, ShieldCheck, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { Button, Card, CheckList, FaqAccordion, PageHero, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BookDemoSection from '@/components/BookDemoSection';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';

// AUTO-GENERATED by the on-brand page generator. Real components only.
const ICONS = [Radar, Clock, Activity, MessageSquareWarning, TrendingUp, ShieldCheck, Sparkles, Target, BarChart3, Zap];
const METRICS = [{"value":"25%","label":"Increase in customer engagement"},{"value":"30%","label":"Boost in sales conversions"},{"value":"95%","label":"Customer satisfaction rate"}];
const PROBLEMS = ["Inadequate social media monitoring","Ineffective customer feedback analysis","Inefficient customer support systems"];
const CAPABILITIES = [{"title":"Social Media Monitoring","body":"Stay on top of social media conversations with our advanced monitoring tools"},{"title":"Customer Feedback Analysis","body":"Gain valuable insights from customer feedback with our AI-powered analysis"},{"title":"Customer Support Systems","body":"Streamline your customer support with our efficient and effective systems"},{"title":"Personalized Customer Experiences","body":"Delight your customers with personalized experiences tailored to their needs"}];
const FAQS = [{"q":"What is Locobuzz?","a":"Locobuzz is an AI-powered customer experience platform designed to help businesses shine brighter"},{"q":"How does Locobuzz work?","a":"Locobuzz uses advanced AI algorithms to monitor social media, analyze customer feedback, and streamline customer support"},{"q":"Is Locobuzz easy to use?","a":"Yes, Locobuzz is designed to be user-friendly and intuitive, making it easy for businesses to get started"},{"q":"What kind of support does Locobuzz offer?","a":"Locobuzz offers comprehensive support, including training, documentation, and dedicated customer support teams"}];

export default function GeneratedBody() {
  return (
    <div>
      <span className="sr-only">{"Discover how Locobuzz can help you shine brighter this Diwali season, try now and illuminate your customer experience!"}</span>

      {/* 1 — Hero */}
      <PageHero
        align="center"
        eyebrow={"Limited Time Offer"}
        title={<>{"Illuminate your "}<span className="serif-accent italic">{"Diwali"}</span>{" sales with AI-powered customer experience"}</>}
        subtitle={"Unlock the secrets to sparkling customer experiences this festive season with Locobuzz"}
        actions={<Button variant="cta" size="lg" href="#book-demo">{"Get Started Today"}</Button>}
      />

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={3} />

      {/* 3 — Problem / solution */}
      <PageSection bg="tint">
        <div className="lp-split">
          <SectionTitle
            align="left"
            eyebrow={"Common Challenges"}
            title={<>{"Are you struggling with "}<span className="serif-accent italic">{"inefficient"}</span>{" customer experience management?"}</>}
            subtitle={"Don't let these common pitfalls hold you back from sparkling customer experiences"}
          />
          <Card padding="xl" radius="2xl">
            <CheckList items={PROBLEMS} />
          </Card>
        </div>
      </PageSection>

      {/* 4 — Capability cards */}
      <PageSection bg="page">
        <SectionTitle
          align="center"
          eyebrow={"Powerful Features"}
          title={<>{"Unlock the power of "}<span className="serif-accent italic">{"AI-driven"}</span>{" customer experience management"}</>}
          subtitle={"Discover how Locobuzz can help you shine brighter this Diwali season"}
        />
        <motion.div className="lp-grid" variants={staggerContainer(0.08)} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {CAPABILITIES.map((cap, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div key={cap.title} variants={fadeUp}>
                <Card padding="xl" radius="2xl" hover className="lp-card">
                  <span className="lp-icon" aria-hidden="true">
                    <Icon size={22} color="var(--brand)" strokeWidth={2.2} />
                  </span>
                  <h3 className="lp-card-title">{cap.title}</h3>
                  <p className="lp-card-body">{cap.body}</p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </PageSection>

      {/* 5 — FAQ */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle align="center" title={<>{"Have questions about "}<span className="serif-accent italic">{"Locobuzz"}</span>{"? We've got answers!"}</>} />
        <FaqAccordion items={FAQS} columns={2} name="generated-faq" />
      </PageSection>

      {/* 6 — Book a demo (always last) */}
      <BookDemoSection />

      <InlineStyle css={`
        .lp-split { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        .lp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lp-card { height: 100%; }
        .lp-icon { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: var(--radius-lg); background: var(--brand-subtle); border: 1px solid var(--brand-border); margin-bottom: 16px; }
        .lp-card-title { font-size: 19px; font-weight: 700; color: var(--text-heading, #0D1B3E); margin: 0 0 8px; letter-spacing: -0.01em; }
        .lp-card-body { font-size: 14.5px; line-height: 1.65; color: var(--text-body); margin: 0; }
        @media (max-width: 900px) { .lp-split { grid-template-columns: 1fr; gap: 32px; } .lp-grid { grid-template-columns: 1fr; } }
      `} />
    </div>
  );
}
